import { useEffect, useState } from 'react'
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material'
import { inventorySyncAdminService } from '../../services'

const DEBOUNCE_MS = 350

export function formatArticleOption(product) {
  if (!product) return null
  const sku = String(product.article_code || product.sku || '').trim()
  const name = String(product.product_name || product.name || 'Untitled').trim()
  return {
    id: String(product.id),
    sku,
    product_name: name,
    label: sku ? `${name} (SKU ${sku})` : name,
  }
}

/**
 * Searchable Article picker (product name or SKU).
 * Value shape: { id, sku, product_name, label } | null
 */
export default function ArticleAutocomplete({
  value,
  onChange,
  label = 'Article',
  required = false,
  disabled = false,
  size = 'small',
  fullWidth = true,
  helperText,
}) {
  const [inputValue, setInputValue] = useState(value?.label || '')
  const [searchText, setSearchText] = useState('')
  const [options, setOptions] = useState(value ? [value] : [])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (value?.label && value.label !== inputValue && !searchText) {
      setInputValue(value.label)
    }
    if (value && !options.some((o) => o.id === value.id)) {
      setOptions((prev) => [value, ...prev.filter((o) => o.id !== value.id)])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.id, value?.label])

  useEffect(() => {
    let cancelled = false
    const term = searchText.trim()
    if (!term) {
      setOptions(value ? [value] : [])
      return undefined
    }
    const timer = setTimeout(async () => {
      try {
        setLoading(true)
        const data = await inventorySyncAdminService.getProducts({
          search: term,
          page: 1,
          limit: 25,
        })
        if (cancelled) return
        const mapped = (data.items || [])
          .map(formatArticleOption)
          .filter((o) => o && o.sku)
        if (value && !mapped.some((o) => o.id === value.id)) {
          setOptions([value, ...mapped])
        } else {
          setOptions(mapped)
        }
      } catch {
        if (!cancelled) setOptions(value ? [value] : [])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [searchText, value])

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      disabled={disabled}
      loading={loading}
      options={options}
      filterOptions={(opts) => opts}
      value={value}
      inputValue={inputValue}
      getOptionLabel={(option) => option?.label || ''}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      onChange={(_, option) => {
        onChange?.(option)
        setInputValue(option?.label || '')
        if (!option) setSearchText('')
      }}
      onInputChange={(_, next, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(next || '')
          setSearchText(next || '')
          if (reason === 'clear') onChange?.(null)
        } else if (reason === 'reset') {
          setInputValue(next || '')
        }
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body2">{option.product_name}</Typography>
            <Typography variant="caption" color="text.secondary">
              SKU {option.sku}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText || 'Search by article name or SKU'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}
